import { defineStore } from 'pinia'
import { SollicitationsApi, Configuration } from '../api'
import type { AvocatDto } from '../api/models'

// Configuration de l'API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.callaw.avoloi.com'
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
})

// Types pour les données du formulaire
interface ClientSollicitation {
  prenom: string
  nom: string
  email: string
  telephone: string
}

interface FormulaireSollicitation {
  client: ClientSollicitation
}

interface SollicitationsState {
  formulaire: FormulaireSollicitation
  avocatCible: AvocatDto | undefined
  loading: boolean
  error: string | undefined
  sollicitationEnvoyee: boolean
  prescriber?: string
}

const emptyFormulaire = (): FormulaireSollicitation => ({
  client: {
    prenom: '',
    nom: '',
    email: '',
    telephone: ''
  }
})

export const useSollicitationsStore = defineStore('sollicitations', {
  state: (): SollicitationsState => ({
    formulaire: emptyFormulaire(),
    avocatCible: undefined,
    loading: false,
    error: undefined,
    sollicitationEnvoyee: false,
    prescriber: undefined
  }),

  getters: {
    formulaireValide(): boolean {
      return (
        this.formulaire.client.prenom.trim().length > 0 &&
        this.formulaire.client.nom.trim().length > 0 &&
        this.formulaire.client.email.trim().length > 0 &&
        this.formulaire.client.telephone.trim().length > 0 &&
        this.avocatCible !== undefined
      )
    },

    idPrescriber(): number {
      // Mapping des prescribers
      const prescriberMap: Record<string, number> = {
        'call-a-lawyer': 52
      }
      return prescriberMap[this.prescriber || 'call-a-lawyer'] || 52
    }
  },

  actions: {
    initNouveauFormulaire(avocat: AvocatDto | undefined = undefined): void {
      this.resetFormulaire()
      this.avocatCible = avocat
    },

    resetFormulaire(): void {
      this.formulaire = emptyFormulaire()
      this.sollicitationEnvoyee = false
      this.error = undefined
    },

    updateClient(donnees: Partial<ClientSollicitation>): void {
      this.formulaire.client = { ...this.formulaire.client, ...donnees }
    },

    async envoyerSollicitation(): Promise<void> {
      if (!this.formulaireValide) {
        this.error = 'Veuillez compléter tous les champs du formulaire'
        throw new Error('Formulaire incomplet')
      }

      if (!this.avocatCible) {
        this.error = 'Aucun avocat sélectionné'
        throw new Error('Avocat manquant')
      }

      this.loading = true
      this.error = undefined

      try {
        const api = new SollicitationsApi(apiConfig)

        // Formater le numéro de téléphone au format international
        const formatPhoneNumber = (phone: string): string => {
          const numbers = phone.replace(/\D/g, '')
          if (numbers.startsWith('0')) {
            return '+33' + numbers.substring(1)
          }
          if (!phone.startsWith('+')) {
            return '+' + numbers
          }
          return phone
        }

        const body: any = {
          idPrescriber: this.idPrescriber,
          idAvocat: this.avocatCible.id!,
          nom: this.formulaire.client.nom,
          prenom: this.formulaire.client.prenom,
          email: this.formulaire.client.email,
          telephone: formatPhoneNumber(this.formulaire.client.telephone)
        }

        await api.sollicitationsControllerSolliciterAvocat(body)

        this.sollicitationEnvoyee = true
      } catch (error: any) {
        console.error('Erreur lors de l\'envoi de la sollicitation:', error)

        // Gestion détaillée des erreurs
        let errorMessage = 'Une erreur est survenue lors de l\'envoi de votre sollicitation.'

        if (error.response) {
          if (error.response.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.response.status === 400) {
            errorMessage = 'Les données fournies sont invalides. Veuillez vérifier le formulaire.'
          } else if (error.response.status === 404) {
            errorMessage = 'L\'avocat demandé n\'existe pas.'
          } else if (error.response.status >= 500) {
            errorMessage = 'Une erreur serveur est survenue. Veuillez réessayer plus tard.'
          }
        } else if (error.request) {
          errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion internet.'
        }

        this.error = errorMessage
        throw error
      } finally {
        this.loading = false
      }
    }
  }
})

